/**
 * 后端接口语言头（lang）。
 *
 * 后端按请求头 lang 返回对应语言的 message / 错误提示，因此所有发往后端的请求都要带上当前语言。
 * 语言来源：?locale= → localStorage.locale → 默认 en（与 src/utils/i18n.js 的首屏语言优先级一致）。
 */
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from 'src/constants/locales'

export const LANG_HEADER = 'lang'

/**
 * 界面语言码 → 后端可识别的语言码。
 * it-ch 只是后台界面的区分码，对外统一按 it 处理。
 */
const BACKEND_LANG_MAP = {
  'it-ch': 'it'
}

const SUPPORTED_LANGS = new Set([DEFAULT_LOCALE, ...SUPPORTED_LOCALES])

const normalizeLang = value => {
  if (!value || typeof value !== 'string') return ''

  const lang = value.trim().toLowerCase()

  return SUPPORTED_LANGS.has(lang) ? lang : ''
}

/** 当前请求应携带的语言码 */
export const getRequestLang = () => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE

  const queryLocale = new URLSearchParams(window.location.search).get('locale')
  const locale = normalizeLang(queryLocale) || normalizeLang(window.localStorage.getItem('locale')) || DEFAULT_LOCALE

  return BACKEND_LANG_MAP[locale] || locale
}

/** 只给自家后端补 lang 头：第三方接口加自定义头会多出预检请求，可能被对方 CORS 策略拦掉 */
const BACKEND_ORIGINS = new Set(
  [
    process.env.NEXT_PUBLIC_BASE_URL,
    process.env.NEXT_PUBLIC_MY_URL,
    process.env.NEXT_PUBLIC_STRAPI_URL,
    process.env.NEXT_PUBLIC_SPYX_SERVICE_URL
  ].reduce((origins, url) => {
    if (!url) return origins

    try {
      origins.push(new URL(url, 'http://localhost').origin)
    } catch (error) {
      // 环境变量缺失或非法时忽略该地址
    }

    return origins
  }, [])
)

const isBackendUrl = url => {
  try {
    return BACKEND_ORIGINS.has(new URL(url, window.location.href).origin)
  } catch (error) {
    return false
  }
}

/**
 * 给浏览器端所有 fetch 请求补上 lang 头。
 * 后端调用以 axios 封装为主，少量页面仍直接用 fetch（支付、评论等），这里统一兜住。
 * 只处理字符串 / URL 地址；Request 对象交给各自的请求封装处理。
 */
export const installLangHeaderForFetch = () => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return
  if (window.__spyxLangHeaderInstalled) return

  window.__spyxLangHeaderInstalled = true

  const originalFetch = window.fetch

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : ''

    if (!url || !isBackendUrl(url)) return originalFetch.call(this, input, init)

    const headers = new Headers(init && init.headers)
    if (headers.has(LANG_HEADER)) return originalFetch.call(this, input, init)

    headers.set(LANG_HEADER, getRequestLang())

    return originalFetch.call(this, input, { ...init, headers })
  }
}
